import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ClipboardCheck } from 'lucide-react';
import { useDataProvider, useNotify, useRecordContext, useRefresh } from 'ra-core';
import { useState } from 'react';
import { BoM } from '../types';

const CHECKLIST_ITEMS = [
    { id: 'materials_sourced', label: 'Materials Sourced' },
    { id: 'drawings_approved', label: 'Drawings Approved' },
    { id: 'qa_specs_ready', label: 'QA Specifications Ready' },
    { id: 'production_scheduled', label: 'Production Scheduled' },
];

export const PreProductionChecklist = () => {
    const record = useRecordContext<BoM>();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [loading, setLoading] = useState(false);

    if (!record) return null;

    const currentChecklist = Array.isArray(record.readiness_checklist) ? record.readiness_checklist : [];

    const handleToggle = async (itemId: string, checked: boolean) => {
        setLoading(true);
        try {
            let updatedChecklist = [...currentChecklist];
            if (checked && !updatedChecklist.includes(itemId)) {
                updatedChecklist.push(itemId);
            } else if (!checked) {
                updatedChecklist = updatedChecklist.filter(id => id !== itemId);
            }

            await dataProvider.update('boms', {
                id: record.id,
                data: { readiness_checklist: updatedChecklist },
                previousData: record,
            });

            refresh();
            notify('Checklist updated', { type: 'success' });
        } catch (error) {
            notify('Failed to update checklist', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const is100Percent = CHECKLIST_ITEMS.every(item => currentChecklist.includes(item.id));

    return (
        <Card>
            <CardHeader className="pb-3 border-b bg-muted/50">
                <div className="flex justify-between items-center">
                    <CardTitle className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                        <ClipboardCheck className="w-4 h-4" /> Pre-Production Readiness
                    </CardTitle>
                    {is100Percent && (
                        <span className="text-xs font-bold text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
                            100% Ready
                        </span>
                    )}
                </div>
            </CardHeader>
            <CardContent className="pt-6">
                <div className="space-y-4">
                    {CHECKLIST_ITEMS.map(item => {
                        const isChecked = currentChecklist.includes(item.id);
                        return (
                            <div key={item.id} className="flex flex-row items-start space-x-3">
                                <Checkbox 
                                    id={item.id}
                                    checked={isChecked}
                                    onCheckedChange={(checked) => handleToggle(item.id, checked as boolean)}
                                    disabled={loading}
                                    className="mt-0.5"
                                />
                                <div className="space-y-1">
                                    <label 
                                        htmlFor={item.id}
                                        className={`text-sm font-medium leading-none cursor-pointer ${isChecked ? 'text-foreground' : 'text-muted-foreground'}`}
                                    >
                                        {item.label}
                                    </label>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
};
